/**
 * Custom sidebar reducer.
 *
 * Overrides the default Volto sidebar reducer to keep certain
 * "technical" fieldsets collapsed by default when the edit form loads.
 * Users can still expand them manually by clicking.
 *
 * How it works:
 * - Volto's Form.jsx dispatches SET_METADATA_FIELDSETS in two scenarios:
 *   1. Bulk init (constructor / schema change): sends ALL fieldset IDs
 *   2. User toggle (click): sends xor(current, [clickedId]) — exactly ±1 item
 *
 * We detect the user toggle case by checking if the incoming array differs
 * from the current state by exactly one added OR one removed element (a proper
 * subset/superset relationship). Everything else is treated as a bulk init
 * where we apply the collapsed-by-default filter.
 */

import union from 'lodash/union';

import {
  SET_METADATA_FIELDSETS,
  SET_METADATA_FOCUS,
  RESET_METADATA_FOCUS,
  SET_SIDEBAR_TAB,
} from '@plone/volto/constants/ActionTypes';

const initialState = {
  tab: 0,
  metadataFieldsets: [],
  metadataFieldFocus: '',
};

// Fieldsets that should remain collapsed by default on bulk init.
// These contain technical/internal information not useful for typical editing.
// Users can still manually expand them.
const COLLAPSED_BY_DEFAULT = [
  'source',
  'metadata',
  'internals',
  'Migration',
  'settings',
];

/**
 * Checks if `incoming` is the result of xor(current, [singleItem]).
 * xor adds an item not in the array, or removes an item that is in the array.
 * This means the result either:
 *   - has exactly 1 new item and all current items (add case), or
 *   - is missing exactly 1 current item and has no new items (remove case)
 */
function isUserToggle(current, incoming) {
  const added = incoming.filter((id) => !current.includes(id));
  const removed = current.filter((id) => !incoming.includes(id));
  return (
    (added.length === 1 && removed.length === 0) ||
    (added.length === 0 && removed.length === 1)
  );
}

export default function sidebar(state = initialState, action = {}) {
  switch (action.type) {
    case SET_METADATA_FIELDSETS: {
      const fieldsets = Array.isArray(action.fieldsets) ? action.fieldsets : [];

      if (isUserToggle(state.metadataFieldsets, fieldsets)) {
        // User clicked an accordion — pass through unchanged
        return {
          ...state,
          metadataFieldsets: fieldsets,
        };
      }

      // Bulk set (form init or schema change) — collapse technical fieldsets
      return {
        ...state,
        metadataFieldsets: fieldsets.filter(
          (id) => !COLLAPSED_BY_DEFAULT.includes(id),
        ),
      };
    }
    case SET_METADATA_FOCUS:
      return {
        ...state,
        metadataFieldsets: union(state.metadataFieldsets, [action.fieldset]),
        metadataFieldFocus: action.field,
      };
    case RESET_METADATA_FOCUS:
      return {
        ...state,
        metadataFieldFocus: '',
      };
    case SET_SIDEBAR_TAB:
      return {
        ...state,
        tab: action.index,
      };
    default:
      return state;
  }
}
